import React, { useState, useEffect } from 'react'
import { Routes, Route, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import '../../styles/client-pages.css'

// ── Inline SVG icons ──────────────────────────────────────────────────────────
const IcSearch   = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
const IcRocket   = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/></svg>
const IcDomain   = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
const IcCard     = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
const IcEdit     = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
const IcActivity = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
const IcMsg      = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
const IcChevron  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
const IcSend     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
const IcPlus     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
const IcBack     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
const IcPaper    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
const IcArrow    = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>

// ── FAQ data ──────────────────────────────────────────────────────────────────
const FAQS = [
  {
    q: 'How do I request a change to my site?',
    a: 'Head to the Requests page and click "New request". Describe what you\'d like changed — you can attach images or files too. Your team typically replies within 4 hours and handles the edit for you.',
  },
  {
    q: 'Can I edit the website myself?',
    a: 'Yes! Click "Edit my site" in the sidebar to open the visual editor. You can change text and images directly by clicking on them. Anything you\'re unsure about, just send us a request instead.',
  },
  {
    q: 'When will my changes go live?',
    a: 'Most edits are published the same day. You\'ll see a status on each request — Open, In Progress, then Done — and get a message from your team when it\'s live.',
  },
  {
    q: 'How do I add a custom email address?',
    a: 'Go to the Domain page and use the "Set up email forwarding" card to create an address like hello@yourdomain.com that forwards to your personal inbox.',
  },
  {
    q: 'How do I change my plan or payment method?',
    a: 'Everything billing-related lives on the Billing page. You can compare plans, upgrade or downgrade (changes are prorated), and update your card there.',
  },
]

const CATEGORIES = [
  { icon: <IcRocket />,   name: 'Getting Started', desc: '8 articles',  tag: 'getting-started' },
  { icon: <IcDomain />,   name: 'Domain & DNS',    desc: '6 articles',  tag: 'domain' },
  { icon: <IcCard />,     name: 'Billing',          desc: '9 articles',  tag: 'billing' },
  { icon: <IcEdit />,     name: 'Editor Help',      desc: '14 articles', tag: 'editor' },
  { icon: <IcActivity />, name: 'Performance',      desc: '5 articles',  tag: 'performance' },
  { icon: <IcMsg />,      name: 'Contact Support',  desc: 'Open a ticket', tag: 'contact' },
]

const POPULAR_ARTICLES = [
  'How to change text and images on your site',
  'Adding, removing and reordering pages',
  'Connecting a domain you already own',
  'Understanding your visitor analytics',
  'Setting up a contact form',
]

// ── Time ago helper ───────────────────────────────────────────────────────────
function timeAgo(ts) {
  const diff = Date.now() - new Date(ts)
  const m = Math.floor(diff / 60000)
  const h = Math.floor(diff / 3600000)
  const d = Math.floor(diff / 86400000)
  if (m < 1)  return 'Just now'
  if (m < 60) return `${m}m ago`
  if (h < 24) return `${h}h ago`
  if (d < 7)  return `${d}d ago`
  return new Date(ts).toLocaleDateString()
}

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS = {
  open:        { label: 'Open',        cls: 'badge-open' },
  in_progress: { label: 'In Progress', cls: 'badge-in_progress' },
  resolved:    { label: 'Resolved',    cls: 'badge-resolved' },
  closed:      { label: 'Closed',      cls: 'badge-closed' },
}

// ── Support ticket list + detail ──────────────────────────────────────────────
const SupportTicketsList = () => {
  const [searchQuery, setSearchQuery]         = useState('')
  const [statusFilter, setStatusFilter]       = useState('all')
  const [selectedTicketId, setSelectedTicketId] = useState(null)
  const queryClient = useQueryClient()
  const [searchParams]                        = useSearchParams()

  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) setSelectedTicketId(openId)
  }, [searchParams])

  // Preserved API call
  const { data: tickets = [], isLoading, error } = useQuery(
    ['support-tickets', statusFilter],
    () => apiService.get('/support/tickets', {
      params: { status: statusFilter !== 'all' ? statusFilter : undefined }
    }).then(res => res.data),
    { staleTime: 2 * 60 * 1000, retry: 1 }
  )

  const filteredTickets = tickets.filter(t =>
    t.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (error) {
    return (
      <div className="card card-pad" style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ color: '#ef4444', fontSize: 14 }}>Failed to load support tickets.</div>
        <button className="ds-btn ds-btn-ghost ds-btn-sm" style={{ marginTop: 12 }} onClick={() => queryClient.invalidateQueries('support-tickets')}>Try again</button>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Redirect to help hub by default if no tickets */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div className="eyebrow">Your account</div>
          <h1 style={{ fontSize: 26, fontWeight: 600, margin: 0, color: 'var(--text)' }}>Support Tickets</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>Track your open support requests</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/client/support/help" style={{ textDecoration: 'none' }}>
            <button className="ds-btn ds-btn-ghost">Help center</button>
          </Link>
          <Link to="/client/support/new" style={{ textDecoration: 'none' }}>
            <button className="ds-btn ds-btn-primary"><IcPlus /> New ticket</button>
          </Link>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: 320 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}><IcSearch /></span>
          <input
            placeholder="Search tickets…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 7, paddingBottom: 7, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, fontSize: 13, color: 'var(--text)', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
          />
        </div>
        {['all', 'open', 'in_progress', 'resolved', 'closed'].map(f => (
          <button key={f} className={`requests-filter-btn${statusFilter === f ? ' active' : ''}`} onClick={() => setStatusFilter(f)}>
            {f === 'all' ? 'All' : f === 'in_progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Ticket list */}
      {isLoading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>Loading tickets…</div>
      ) : filteredTickets.length === 0 ? (
        <div className="card card-pad" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <IcMsg />
          <div style={{ fontSize: 14, fontWeight: 500, marginTop: 12, color: 'var(--text)' }}>
            {searchQuery || statusFilter !== 'all' ? 'No matching tickets' : 'No support tickets yet'}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>
            {searchQuery || statusFilter !== 'all' ? 'Try adjusting your search or filter.' : 'Create a ticket to get help from our team.'}
          </div>
          <Link to="/client/support/new" style={{ textDecoration: 'none' }}>
            <button className="ds-btn ds-btn-primary ds-btn-sm" style={{ marginTop: 14 }}>Create support ticket</button>
          </Link>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredTickets.map(ticket => {
            const s = STATUS[ticket.status] || STATUS.open
            return (
              <div
                key={ticket.id}
                style={{ padding: '14px 16px', border: '1px solid var(--border)', borderRadius: 12, background: 'var(--surface)', cursor: 'pointer', transition: 'box-shadow .12s' }}
                onClick={() => setSelectedTicketId(ticket.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span className={`request-badge ${s.cls}`}>{s.label}</span>
                  <span style={{ fontSize: 10.5, color: 'var(--text-faint)', fontFamily: 'monospace', marginLeft: 'auto' }}>{timeAgo(ticket.created_at)}</span>
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{ticket.subject}</div>
                {ticket.description && (
                  <div style={{ fontSize: 12, color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ticket.description}</div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Help hub ──────────────────────────────────────────────────────────────────
const HelpHub = () => {
  const [query, setQuery] = useState('')
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', message: '' })
  const [submitting, setSubmitting] = useState(false)

  const handleContactSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    // Simulate submit
    await new Promise(r => setTimeout(r, 800))
    toast.success('Message sent! We\'ll get back to you soon.')
    setContactForm({ name: '', email: '', subject: '', message: '' })
    setSubmitting(false)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingBottom: 40 }}>

      {/* Search hero */}
      <div className="help-search-hero">
        <h1>How can we help?</h1>
        <div className="help-search-wrap">
          <div className="help-search-input">
            <IcSearch />
            <input
              placeholder='Search articles — e.g. "change my logo"'
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <div className="help-search-tags">
            {['Editing text', 'Adding a page', 'Custom domain', 'Billing', 'Analytics'].map(t => (
              <button key={t} className="help-tag" onClick={() => setQuery(t)}>{t}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Category grid */}
      <div className="help-categories">
        {CATEGORIES.map(cat => (
          <div key={cat.tag} className="help-category-card">
            <div className="help-category-icon">{cat.icon}</div>
            <div className="help-category-name">{cat.name}</div>
            <div className="help-category-desc">{cat.desc}</div>
          </div>
        ))}
      </div>

      {/* Popular articles + Contact card */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 14, alignItems: 'start' }}>

        {/* Popular articles */}
        <div className="card card-pad">
          <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: '0 0 14px' }}>Popular articles</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {POPULAR_ARTICLES.map((a, i) => (
              <button key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: 'none', background: 'transparent', borderRadius: 8, cursor: 'pointer', width: '100%', textAlign: 'left', transition: 'background .1s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ width: 26, height: 26, borderRadius: 8, background: 'rgba(108,99,255,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', flexShrink: 0 }}>
                  <IcPaper />
                </span>
                <span style={{ flex: 1, fontSize: 13, color: 'var(--text)' }}>{a}</span>
                <span style={{ color: 'var(--text-muted)' }}><IcArrow /></span>
              </button>
            ))}
          </div>
        </div>

        {/* Contact support */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="card card-pad">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: -4 }}>
                {['#6c63ff', '#22c55e', '#f59e0b'].map((c, i) => (
                  <div key={i} style={{ width: 28, height: 28, borderRadius: '50%', background: c, border: '2px solid var(--surface)', marginLeft: i > 0 ? -8 : 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 10, fontWeight: 700 }}>
                    {['S', 'T', 'R'][i]}
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Your WebNest team</div>
                <div style={{ fontSize: 11.5, color: '#22c55e' }}>Online · replies in ~1 hour</div>
              </div>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, marginBottom: 14 }}>
              The same people who built your site. Send a message and a real human gets back to you.
            </div>
            <Link to="/client/requests/new" style={{ textDecoration: 'none', display: 'block' }}>
              <button className="ds-btn ds-btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
                <IcSend /> Start a conversation
              </button>
            </Link>
            <Link to="/client/support/new" style={{ textDecoration: 'none', display: 'block', marginTop: 8 }}>
              <button className="ds-btn ds-btn-ghost" style={{ width: '100%', justifyContent: 'center' }}>
                Open a support ticket
              </button>
            </Link>
          </div>

          <div className="card card-pad">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', margin: 0 }}>System status</h2>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: '#22c55e', background: 'rgba(34,197,94,.12)', padding: '2px 8px', borderRadius: 20 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} /> Operational
              </span>
            </div>
            {['Website hosting', 'Visual editor', 'Analytics'].map(svc => (
              <div key={svc} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '5px 0' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                <span style={{ flex: 1, color: 'var(--text-muted)' }}>{svc}</span>
                <span style={{ fontSize: 11.5, color: 'var(--text-faint)' }}>Operational</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ accordion (CSS-only via details/summary) */}
      <div className="card card-pad">
        <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: '0 0 14px' }}>Frequently asked</h2>
        {FAQS.map((f, i) => (
          <details key={i} className="faq-item">
            <summary>
              {f.q}
              <span className="faq-chevron"><IcChevron /></span>
            </summary>
            <div className="faq-answer">{f.a}</div>
          </details>
        ))}
      </div>

      {/* Contact form */}
      <div className="card card-pad">
        <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>Send us a message</h2>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 18 }}>We'll reply to your email within 24 hours.</div>
        <form onSubmit={handleContactSubmit} className="help-contact-form">
          <div className="help-contact-row">
            <div className="help-field">
              <label>Your name</label>
              <input type="text" value={contactForm.name} onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))} placeholder="Jane Smith" required />
            </div>
            <div className="help-field">
              <label>Email address</label>
              <input type="email" value={contactForm.email} onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))} placeholder="you@example.com" required />
            </div>
          </div>
          <div className="help-field">
            <label>Subject</label>
            <input type="text" value={contactForm.subject} onChange={e => setContactForm(p => ({ ...p, subject: e.target.value }))} placeholder="Briefly describe your issue" required />
          </div>
          <div className="help-field">
            <label>Message</label>
            <textarea rows={4} value={contactForm.message} onChange={e => setContactForm(p => ({ ...p, message: e.target.value }))} placeholder="Tell us more…" required />
          </div>
          <div>
            <button type="submit" className={`ds-btn ds-btn-primary${submitting ? ' ds-btn-loading' : ''}`} disabled={submitting}>
              <IcSend /> {submitting ? 'Sending…' : 'Send message'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── New support ticket form ────────────────────────────────────────────────────
const NewSupportTicket = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formData, setFormData]   = useState({ subject: '', message: '', priority: 'medium' })
  const [attachments, setAttachments] = useState([])

  // Preserved API call
  const createTicketMutation = useMutation(
    (data) => {
      const fd = new FormData()
      fd.append('subject', data.subject)
      fd.append('message', data.message)
      fd.append('priority', data.priority)
      if (data.screenshot) fd.append('screenshot', data.screenshot)
      return apiService.post('/support', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
    {
      onSuccess: () => {
        toast.success('Support ticket created successfully!')
        queryClient.invalidateQueries('support-tickets')
        navigate('/client/support')
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to create support ticket')
    }
  )

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error('Please fill in all required fields')
      return
    }
    createTicketMutation.mutate({ ...formData, screenshot: attachments[0] })
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, color: 'var(--text)' }}>Create Support Ticket</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>Tell us about the issue you're experiencing</div>
        </div>
        <Link to="/client/support" style={{ textDecoration: 'none' }}>
          <button className="ds-btn ds-btn-ghost"><IcBack /> Back to support</button>
        </Link>
      </div>

      <div className="card card-pad" style={{ maxWidth: 600 }}>
        <form onSubmit={handleSubmit} className="help-contact-form">
          <div className="help-field">
            <label>Subject *</label>
            <input type="text" value={formData.subject} onChange={e => setFormData(p => ({ ...p, subject: e.target.value }))} placeholder="Brief description of your issue" required />
          </div>
          <div className="help-field">
            <label>Priority</label>
            <select value={formData.priority} onChange={e => setFormData(p => ({ ...p, priority: e.target.value }))}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div className="help-field">
            <label>Message *</label>
            <textarea rows={5} value={formData.message} onChange={e => setFormData(p => ({ ...p, message: e.target.value }))} placeholder="Please provide detailed information about your issue…" required style={{ minHeight: 100 }} />
          </div>
          <div className="help-field">
            <label>Screenshot (Optional)</label>
            <div style={{ border: '2px dashed var(--border)', borderRadius: 10, padding: 16, textAlign: 'center' }}>
              <input type="file" id="ticket-screenshot" accept="image/*" onChange={e => setAttachments(Array.from(e.target.files))} style={{ display: 'none' }} />
              <label htmlFor="ticket-screenshot" style={{ cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13 }}>
                Click to upload a screenshot
                <div style={{ fontSize: 11.5, marginTop: 4 }}>PNG, JPG up to 5MB</div>
              </label>
              {attachments.length > 0 && <div style={{ marginTop: 8, fontSize: 12.5, color: '#22c55e' }}>{attachments[0].name}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" className="ds-btn ds-btn-primary" disabled={createTicketMutation.isLoading}>
              <IcSend /> {createTicketMutation.isLoading ? 'Creating…' : 'Create ticket'}
            </button>
            <Link to="/client/support" style={{ textDecoration: 'none' }}>
              <button type="button" className="ds-btn ds-btn-ghost">Cancel</button>
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Router wrapper ────────────────────────────────────────────────────────────
const ClientSupport = () => (
  <Routes>
    <Route index element={<SupportTicketsList />} />
    <Route path="help" element={<HelpHub />} />
    <Route path="new"  element={<NewSupportTicket />} />
  </Routes>
)

export default ClientSupport
